// MUST stay the first import, ahead of ./logger-init, for two reasons. It takes
// a candidate's upload token out of the address before anything that reports
// addresses has started. And it keeps the browser's own fetch before the logger
// wraps window.fetch: the logger reports request headers to Application
// Insights, and that token travels as a header. Checked by
// src/views/public/__tests__/documentLinkCapture.test.ts.
import "./views/public/documentLink";
import "./logger-init";
import { useEffect, useState, lazy, Suspense, useRef } from "react";
import { BrowserRouter, Routes, Route } from "react-router";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { SkyProvider } from "@skypointcloud/skypoint-ui";
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { createMsalInstance } from "./config/msalConfig";
import { authService as auth, setMsalInstance } from "./utils/authService";
import { logger } from "./services/logger.service";
import SkypointLogoLoader from "./components/SkypointLogoLander/SkypointLogoLoader";
import { ProtectedRoute } from "./components/ProtectedRoute";
const SharedConversationPage = lazy(() =>
  import("./views/skyagent/SharedConversationPage").then((m) => ({
    default: m.SharedConversationPage,
  })),
);
import { useB2CPolicies } from "./hooks/useB2CPolicies";
import { IframeAuthError } from "./components/IframeAuthError";
import { ROUTES } from "./components/shared/constant";
import { ThemeSync } from "./components/theme/ThemeSync";
import { isThemedPath, readThemePreference, type ThemePreference } from "./lib/theme";
import Callback from "./components/callback";
import { store, persistor } from "./store";
import "@skypointcloud/skypoint-ui/styles.css";
import "./css/skypointcloud.css";
import "./index.css";
// Last, so its unlayered rules can override the unlayered overrides in
// index.css. See the header comment in the file for what lives there.
import "./styles/ui-polish.css";
// Every Performance page prints its content only (no header bar, no side navigation); inert
// on hire pages, keyed on data-product="skyperformance". Before the palette, which stays last.
import "./products/performance/views/print.css";
// skyPerformance IT's palette (spec 2026-10-26 §4.6). Inert unless <html> carries
// data-product="skyperformance_it"; never on together with skyPerformance's, so before it, which
// stays last.
import "./products/performance-it/theme.css";
// skyPerformance's palette. Inert unless <html> carries data-product="skyperformance"
// (components/products/productTheme.ts); after everything else so its scoped tokens win.
import "./products/performance/theme.css";
import AppNew from "./AppNew";

import { MessageBlock } from "./components/MessageBlock";
import { ConfirmationProvider } from "./components/shared/confirm";
import { getAndClearOAuthReturnPath, captureOAuthCallbackStatus } from "./utils/oauthReturnHandler";

captureOAuthCallbackStatus();

const PublicSurveyPage = lazy(() => import("./views/surveys/public/PublicSurveyPage"));
const PacketSigningPage = lazy(() => import("./views/public/PacketSigningPage"));
const ReuploadVideoPage = lazy(() => import("./views/public/ReuploadVideoPage"));
const InterviewCancelPage = lazy(() => import("./views/public/InterviewCancelPage"));
const EasyBookPage = lazy(() => import("./views/public/EasyBookPage"));
const DocumentRequestPage = lazy(() => import("./views/public/DocumentRequestPage"));
const OnboardingJoinerPage = lazy(() => import("./views/public/onboardingJoin/JoinerPage"));
const DocumentRequestPreviewPage = lazy(() => import("./views/public/DocumentRequestPreviewPage"));
const CandidateApp = lazy(() => import("./views/candidate/CandidateApp"));

const SKY_BRAND_COLOR = "#FFB31C";

// The saved Light/Dark/System choice (default: follow the OS), as in skySchedule.
// index.html applied it before paint; SkyProvider takes over from here. Public
// pages and the candidate portal always stay light (see src/lib/theme.ts).
const APP_THEME_MODE: ThemePreference = isThemedPath(
  window.location.href,
  window.location.pathname,
  sessionStorage.getItem("isCandidateFlow") === "true",
)
  ? readThemePreference()
  : "light";

declare global {
  interface Window {
    instanceCultureName: string;
    instanceTimezone: string;
    isFullAccess: boolean;
    isPlatformAdmin: boolean;
    isTenantAdmin: boolean;
    isInstanceAdmin: boolean;
    /**
     * Stripe.js v3, loaded by the script tag in index.html. No TypeScript
     * reads it today; `unknown` records that the global exists without
     * claiming a shape, so the first real use has to narrow deliberately.
     */
    Stripe: unknown;
    /** Login timing, set and cleared by AppNew. Optional so it can be deleted. */
    __SKY_LOGIN_PERF__?: { startTime: number; appMounted: number };
  }
}

const ApplicationBlock = () => {
  const { instance } = useMsal();

  // Account-enabled state comes from idTokenClaims — no network round trip needed.
  // AuthenticatedTemplate already guarantees an authenticated account above this.
  const currentUser = auth?.currentUser();
  const isAccountEnabled = currentUser?.isAccountEnabled !== false;

  if (!isAccountEnabled) {
    return (
      <MessageBlock>
        <h3>
          Your account has been locked. Contact your support person to unlock it, then try again.
        </h3>
        <h4>
          Click here to{" "}
          <a
            href="#"
            onClick={() => instance.logoutRedirect()}
          >
            Sign out
          </a>
        </h4>
      </MessageBlock>
    );
  }

  return (
    // No transitions: react-router wraps every location change in startTransition by default,
    // and inside a transition React keeps the OLD page on screen (and never shows AppLayout's
    // <Suspense fallback={<PageSkeleton />}>) until the new page's lazy chunk has loaded. The URL
    // changed at once but the page looked frozen until then, for seconds on a first visit or a
    // slow network. Off, the page-shaped skeleton shows the moment a link is clicked.
    <BrowserRouter unstable_useTransitions={false}>
      <Routes>
        <Route
          path={ROUTES.CALLBACK}
          element={<Callback />}
        />
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <AppNew />
            </ProtectedRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

/**
 * Login Handler Component
 * Automatically triggers login when user is not authenticated
 * Uses popup for iframes, redirect for top-level windows
 *
 * Performance Optimizations:
 * - Fast path: No cached accounts → skip ssoSilent, go directly to login (~500-1000ms savings)
 * - Interaction guard: Skip login attempts when MSAL has interaction in progress
 */
const LoginHandler = () => {
  const { instance, accounts, inProgress } = useMsal();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isIframeError, setIsIframeError] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const loginAttempted = useRef(false);

  const isInIframe = window.self !== window.top;

  useEffect(() => {
    if (inProgress !== "none") {
      return;
    }

    if (accounts.length === 0 && !loginError && !isLoggingIn && !loginAttempted.current) {
      loginAttempted.current = true;
      setIsLoggingIn(true);

      // Save the current URL so callback.tsx can redirect here after login
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath && currentPath !== "/" && currentPath !== "/callback") {
        localStorage.setItem("skyhire_redirect_after_login", currentPath);
      }

      auth.login().catch((error) => {
        logger.error("Login initiation failed", error);
        setIsLoggingIn(false);
        loginAttempted.current = false;

        const authError = error as {
          errorCode?: string;
          errorMessage?: string;
          message?: string;
        };
        const errorMsg = authError?.errorMessage || authError?.message || "";

        if (
          errorMsg.includes("iframed") ||
          errorMsg.includes("brokered") ||
          errorMsg.includes("redirect APIs") ||
          authError?.errorCode === "redirect_in_iframe"
        ) {
          setIsIframeError(true);
          setLoginError(errorMsg);
        } else if (authError?.errorCode === "popup_window_error") {
          setLoginError("Popup was blocked. Please allow popups for this site and try again.");
        } else if (authError?.errorCode === "user_cancelled") {
          setLoginError("Login was cancelled. Click below to try again.");
        } else if (authError?.errorCode === "popup_blocked_reload_required") {
          setLoginError("Please enable popups or reload the page to sign in.");
        } else if (authError?.errorCode === "interaction_in_progress") {
          setLoginError("Authentication is already in progress. Please reload the page.");
        } else {
          if (isInIframe) {
            setIsIframeError(true);
          }
          setLoginError(errorMsg || "Login failed. Please try again.");
        }
      });
    }
  }, [instance, accounts, inProgress, loginError, isLoggingIn, isInIframe]);

  if (loginError || isIframeError) {
    return (
      <IframeAuthError
        errorMessage={loginError || undefined}
        onRetry={() => {
          setLoginError(null);
          setIsIframeError(false);
          setIsLoggingIn(false);
          loginAttempted.current = false;
        }}
      />
    );
  }

  return <SkypointLogoLoader />;
};

/**
 * Authenticated App Wrapper
 * Uses MSAL React templates for authentication state management
 */
const AuthenticatedApp = () => {
  const { inProgress } = useMsal();
  const [oauthReturnHandled, setOauthReturnHandled] = useState(false);

  // Phase 7: Handle B2C policies (password reset, error codes, etc.)
  useB2CPolicies();

  useEffect(() => {
    if (inProgress === "none" && !oauthReturnHandled) {
      const result = getAndClearOAuthReturnPath();
      if (result && window.location.pathname !== result.path.split("?")[0]) {
        let targetUrl = result.path;

        if (result.oauthStatus) {
          const separator = result.path.includes("?") ? "&" : "?";
          targetUrl = `${result.path}${separator}oauth=${result.oauthStatus.status}`;
          if (result.oauthStatus.message) {
            targetUrl += `&message=${encodeURIComponent(result.oauthStatus.message)}`;
          }
        }

        window.location.replace(targetUrl);
      } else {
        setOauthReturnHandled(true);
      }
    }
  }, [inProgress, oauthReturnHandled]);

  // Show loader while MSAL is processing interactions
  if (inProgress === "startup" || inProgress === "handleRedirect") {
    return <SkypointLogoLoader />;
  }

  // Show loader while handling OAuth return
  if (!oauthReturnHandled) {
    return <SkypointLogoLoader />;
  }

  return (
    <>
      {/* Show ApplicationBlock when user is authenticated */}
      <AuthenticatedTemplate>
        <ApplicationBlock />
      </AuthenticatedTemplate>

      {/* Trigger login and show loader when user is not authenticated */}
      <UnauthenticatedTemplate>
        <LoginHandler />
      </UnauthenticatedTemplate>
    </>
  );
};

/**
 * App initialization wrapper
 * Handles MSAL instance creation and initialization
 */
// Lightweight loading spinner for public pages
const SimpleLoader = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
    <div
      style={{
        width: 40,
        height: 40,
        border: "3px solid #f0f0f0",
        borderTop: "3px solid #ffb31c",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
      }}
    />
    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
  </div>
);

const AppWithMsal = ({ msalInstance }: { msalInstance: PublicClientApplication }) => {
  const url = window.location.href;

  // Public survey route - minimal setup, no PersistGate needed
  if (url.includes("/survey/")) {
    return (
      <SkyProvider
        brandColor={SKY_BRAND_COLOR}
        defaultMode="light"
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <Provider store={store}>
          <BrowserRouter>
            <Suspense fallback={<SimpleLoader />}>
              <Routes>
                <Route
                  path="/survey/:instanceId/:token"
                  element={<PublicSurveyPage />}
                />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </Provider>
      </SkyProvider>
    );
  }

  // Public packet signing route - minimal setup, no auth needed
  if (url.includes("/sign/")) {
    return (
      <SkyProvider
        brandColor={SKY_BRAND_COLOR}
        defaultMode="light"
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <Provider store={store}>
          <BrowserRouter>
            <Suspense fallback={<SimpleLoader />}>
              <Routes>
                <Route
                  path="/sign/:accessToken"
                  element={<PacketSigningPage />}
                />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </Provider>
      </SkyProvider>
    );
  }

  // Public video reupload route - tokenized link for anonymous applicants,
  // no auth needed.
  if (url.includes("/reupload/")) {
    return (
      <SkyProvider
        brandColor={SKY_BRAND_COLOR}
        defaultMode="light"
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <BrowserRouter>
          <Suspense fallback={<SimpleLoader />}>
            <Routes>
              <Route
                path="/reupload/:applicationId/:accessToken"
                element={<ReuploadVideoPage />}
              />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </SkyProvider>
    );
  }

  // Candidate portal - uses its own MSAL instance, completely separate from admin.
  // Also handles /callback when isCandidateFlow flag is set (B2C redirect after candidate login).
  if (
    isCandidatePortalPath(window.location.pathname) ||
    (window.location.pathname === "/callback" &&
      sessionStorage.getItem("isCandidateFlow") === "true")
  ) {
    return (
      <Suspense fallback={<SimpleLoader />}>
        <CandidateApp />
      </Suspense>
    );
  }

  // Public interview cancel route - minimal setup, no auth needed
  if (url.includes("/interview/cancel")) {
    return (
      <SkyProvider
        brandColor={SKY_BRAND_COLOR}
        defaultMode="light"
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <Provider store={store}>
          <BrowserRouter>
            <Suspense fallback={<SimpleLoader />}>
              <Routes>
                <Route
                  path="/interview/cancel"
                  element={<InterviewCancelPage />}
                />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </Provider>
      </SkyProvider>
    );
  }

  // Public shared SkyAgent conversation route - minimal setup, no auth needed
  if (url.includes("/share/")) {
    return (
      <SkyProvider
        brandColor={SKY_BRAND_COLOR}
        defaultMode="light"
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <Provider store={store}>
          <BrowserRouter>
            <Suspense fallback={<SimpleLoader />}>
              <Routes>
                <Route
                  path="/share/:shareId"
                  element={<SharedConversationPage />}
                />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </Provider>
      </SkyProvider>
    );
  }

  // Public Easy Book route - minimal setup, no auth needed
  if (url.includes("/easy-book")) {
    return (
      <SkyProvider
        brandColor={SKY_BRAND_COLOR}
        defaultMode="light"
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <Provider store={store}>
          <BrowserRouter>
            <Suspense fallback={<SimpleLoader />}>
              <Routes>
                <Route
                  path="/easy-book"
                  element={<EasyBookPage />}
                />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </Provider>
      </SkyProvider>
    );
  }

  // The preview of that page for the settings card: the same view, made-up
  // data, no link and no request. Its own block so it is light and unsigned,
  // as every public page is.
  if (window.location.pathname.replace(/\/+$/, "") === "/documents/upload/preview") {
    return (
      <SkyProvider
        brandColor={SKY_BRAND_COLOR}
        defaultMode="light"
      >
        <BrowserRouter>
          <Suspense fallback={<SimpleLoader />}>
            <Routes>
              <Route
                path="/documents/upload/preview"
                element={<DocumentRequestPreviewPage />}
              />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </SkyProvider>
    );
  }

  // Public document upload route - a candidate's personal link, no auth needed
  // and no store: the page talks to the public API with plain fetch. The token
  // was in the fragment and is already gone from the address (documentLink.ts).
  if (window.location.pathname.replace(/\/+$/, "") === "/documents/upload") {
    return (
      <SkyProvider
        brandColor={SKY_BRAND_COLOR}
        defaultMode="light"
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <BrowserRouter>
          <Suspense fallback={<SimpleLoader />}>
            <Routes>
              <Route
                path="/documents/upload"
                element={<DocumentRequestPage />}
              />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </SkyProvider>
    );
  }

  // The onboarding joiner's page - a new hire's personal link, no auth and no store, handled like
  // the upload page above: the token was taken out of the address before this loaded
  // (documentLink.ts) and the page talks to the public API with the browser's own fetch.
  if (window.location.pathname.replace(/\/+$/, "") === "/join") {
    return (
      <SkyProvider
        brandColor={SKY_BRAND_COLOR}
        defaultMode="light"
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <BrowserRouter>
          <Suspense fallback={<SimpleLoader />}>
            <Routes>
              <Route
                path="/join"
                element={<OnboardingJoinerPage />}
              />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </SkyProvider>
    );
  }

  // Protected routes wrapped with MsalProvider
  return (
    <SkyProvider
      brandColor={SKY_BRAND_COLOR}
      defaultMode={APP_THEME_MODE}
      toastConfig={{
        position: "top-center",
        maxToasts: 5,
        defaultDuration: 5000,
      }}
    >
      <ThemeSync />
      <ConfirmationProvider>
        <Provider store={store}>
          <PersistGate
            loading={<SkypointLogoLoader />}
            persistor={persistor}
          >
            <MsalProvider instance={msalInstance}>
              <AuthenticatedApp />
            </MsalProvider>
          </PersistGate>
        </Provider>
      </ConfirmationProvider>
    </SkyProvider>
  );
};

/**
 * Root component with async MSAL initialization
 */
/**
 * Is this URL the candidate portal?
 *
 * Tests the PATH SEGMENT, not a substring of the whole URL.
 *
 * `url.includes("/candidate")` matched any URL merely containing that text, so
 * every admin settings page whose slug starts with "candidate" was silently
 * handed to the candidate portal and the recruiter got a login screen. That
 * already broke /settings/candidate-contact-info before this feature existed,
 * and would break every future route named after candidates.
 */
function isCandidatePortalPath(pathname: string): boolean {
  return pathname === "/candidate" || pathname.startsWith("/candidate/");
}

const AppRoot = () => {
  const [msalInstance, setMsalInstanceState] = useState<PublicClientApplication | null>(null);
  const [initError, setInitError] = useState<Error | null>(null);
  // /callback with isCandidateFlow flag means B2C just redirected back from candidate login
  const isCandidateRoute =
    isCandidatePortalPath(window.location.pathname) ||
    (window.location.pathname === "/callback" &&
      sessionStorage.getItem("isCandidateFlow") === "true");

  useEffect(() => {
    // Skip admin MSAL init for candidate routes — candidate has its own MSAL instance.
    // Creating two instances with the same clientId causes MSAL duplicate instance errors.
    if (isCandidateRoute) return;

    // Initialize MSAL instance (v4 requires async initialization)
    createMsalInstance()
      .then((instance) => {
        // Set instance for auth service
        setMsalInstance(instance);
        setMsalInstanceState(instance);
      })
      .catch((error) => {
        logger.error("Failed to initialize MSAL", error);
        setInitError(error);
      });
  }, [isCandidateRoute]);

  // Candidate portal — render directly with its own MSAL instance, no admin MSAL needed
  if (isCandidateRoute) {
    return (
      <Suspense fallback={<SimpleLoader />}>
        <CandidateApp />
      </Suspense>
    );
  }

  if (initError) {
    return (
      <SkyProvider
        brandColor={SKY_BRAND_COLOR}
        defaultMode={APP_THEME_MODE}
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <MessageBlock>
          <h3>Authentication initialization failed</h3>
          <p>Please refresh the page. If the problem persists, contact support.</p>
        </MessageBlock>
      </SkyProvider>
    );
  }

  if (!msalInstance) {
    return (
      <SkyProvider
        brandColor={SKY_BRAND_COLOR}
        defaultMode={APP_THEME_MODE}
        toastConfig={{
          position: "top-center",
          maxToasts: 5,
          defaultDuration: 5000,
        }}
      >
        <SkypointLogoLoader />
      </SkyProvider>
    );
  }

  return <AppWithMsal msalInstance={msalInstance} />;
};

createRoot(document.getElementById("root")!).render(<AppRoot />);
